/**
 * `<VirtualizedList>` — the shared windowed-list primitive (frontend.md §1.5, §5 step 6).
 *
 * D-009 raises the guarantee to 100K notes; frontend.md §1.5 is categorical: "virtualize
 * every long list/table ... no surface renders an unbounded row set". Every long list in
 * the app — search results, entity/tag lists, the inferred-link feed, failed-extraction
 * triage — goes through this component (or `<VirtualizedTable>`).
 *
 * Built on `@tanstack/react-virtual`. The list owns its scroll container; it renders only
 * the visible window plus a small overscan. `onEndReached` fires when the user nears the
 * bottom — the surface uses it to pull the next cursor page (frontend.md §1.5 — paginate,
 * never "all"). `estimateSize` is the per-row height estimate (the virtualizer measures
 * actual rows after mount).
 */

import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface VirtualizedListProps<T> {
  items: T[];
  /** Per-row render. `index` is the absolute index into `items`. */
  renderRow: (item: T, index: number) => React.ReactNode;
  /** Estimated row height in px — the virtualizer refines this by measuring. */
  estimateSize?: number;
  /** Stable key per row. */
  getKey: (item: T, index: number) => string;
  /** Fired when the user scrolls near the end — pull the next page here. */
  onEndReached?: () => void;
  /** How many rows from the end counts as "near" — defaults to 8. */
  endThreshold?: number;
  /** Accessible label for the scroll region. */
  ariaLabel?: string;
  /**
   * 2026-05-18 user story — programmatic "scroll this row to center"
   * signal. When `scrollSignal` changes AND `scrollToIndex` resolves to a
   * valid row index (>= 0 and < items.length), the virtualizer scrolls
   * that row to the centre of the viewport. Use `scrollSignal` as the
   * trigger token (e.g. the noteId of the row to focus) so the effect
   * only fires when the parent actually wants to scroll — not on every
   * incidental re-render. The first scroll signal received after mount
   * IS honoured (we don't suppress the initial value); pass `null` /
   * leave undefined to suppress.
   */
  scrollToIndex?: number;
  scrollSignal?: string | number | null;
}

export function VirtualizedList<T>({
  items,
  renderRow,
  estimateSize = 72,
  getKey,
  onEndReached,
  endThreshold = 8,
  ariaLabel,
  scrollToIndex,
  scrollSignal,
}: VirtualizedListProps<T>): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan: 6,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const lastRow = virtualRows[virtualRows.length - 1];

  // Fire `onEndReached` once when the rendered window reaches the threshold from the end.
  // The surface debounces fetches via `@tanstack/query` (a fetch already in flight is
  // deduped), so a simple effect is safe here.
  useEffect(() => {
    if (!onEndReached || lastRow === undefined) return;
    if (lastRow.index >= items.length - endThreshold) {
      onEndReached();
    }
  }, [lastRow, items.length, endThreshold, onEndReached]);

  // Scroll-to-row signal — deps deliberately `[scrollSignal]` only. The
  // index can change between renders (filter sort etc) without us wanting
  // a scroll; the parent communicates intent via the signal token. The
  // virtualizer's `scrollToIndex({align: 'center'})` measures the row to
  // place it at the centre of the scrollport; if the row is currently
  // unmounted (far off-screen), it mounts + measures it first then scrolls.
  useEffect(() => {
    if (scrollSignal === undefined || scrollSignal === null) return;
    if (scrollToIndex === undefined || scrollToIndex < 0) return;
    if (scrollToIndex >= items.length) return;
    virtualizer.scrollToIndex(scrollToIndex, {
      align: 'center',
      behavior: 'smooth',
    });
    // Intentionally don't include scrollToIndex / items.length / virtualizer
    // in deps: we want this effect to fire ONLY when the parent's signal
    // changes. Adding the others would re-fire on filter typing etc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollSignal]);

  return (
    <div
      ref={scrollRef}
      className="virtualized-list"
      role="list"
      aria-label={ariaLabel}
      style={{ overflow: 'auto', height: '100%' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualRows.map((vr) => {
          const item = items[vr.index];
          if (item === undefined) return null;
          return (
            <div
              key={getKey(item, vr.index)}
              role="listitem"
              data-index={vr.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vr.start}px)`,
              }}
            >
              {renderRow(item, vr.index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
